import { createRouter, createRoute, createRootRoute, redirect, Outlet } from '@tanstack/react-router';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { LeadsPage } from '@/pages/leads/LeadsPage';
import { LeadDetailPage } from '@/pages/leads/LeadDetailPage';
import { LiveCallPage } from '@/pages/calls/LiveCallPage';
import { AppointmentsPage } from '@/pages/appointments/AppointmentsPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { IntegrationsPage } from '@/pages/integrations/IntegrationsPage';
import { AdsPage } from '@/pages/ads/AdsPage';
import { AdminPage } from '@/pages/admin/AdminPage';
import { TemplatesPage } from '@/pages/templates/TemplatesPage';
import { AuditPage } from '@/pages/audit/AuditPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { useAuthStore } from '@/stores/authStore';

// ─── Root ───────────────────────────────────────────────────────────────────
const rootRoute = createRootRoute({
  component: Outlet,
});

// ─── Auth guard ──────────────────────────────────────────────────────────────
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: AppLayout,
});

// ─── Public routes ───────────────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

// ─── Protected routes ────────────────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/',
  component: DashboardPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/dashboard',
  beforeLoad: () => { throw redirect({ to: '/' }); },
  component: DashboardPage,
});

const leadsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/leads',
  component: LeadsPage,
});

const leadDetailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/leads/$leadId',
  component: LeadDetailPage,
});

const callsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/calls',
  component: LiveCallPage,
});

const appointmentsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/appointments',
  component: AppointmentsPage,
});

const documentsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/documents',
  component: DocumentsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/reports',
  component: ReportsPage,
});

const integrationsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/integrations',
  component: IntegrationsPage,
});

const adsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/ads',
  component: AdsPage,
});

const adminRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/admin',
  component: AdminPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/templates',
  component: TemplatesPage,
});

const auditRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/audit',
  component: AuditPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/settings',
  component: SettingsPage,
});

// ─── Route tree ──────────────────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  loginRoute,
  authRoute.addChildren([
    indexRoute,
    dashboardRoute,
    leadsRoute,
    leadDetailRoute,
    callsRoute,
    appointmentsRoute,
    documentsRoute,
    reportsRoute,
    integrationsRoute,
    adsRoute,
    adminRoute,
    templatesRoute,
    auditRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
